import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  AVAILABLE_USER_OPTIONS,
  CLINICIAN_ROLES_ID_LIST,
  IMPARTIAL_WITNESS_ALLOWED_CREATORS_LIST,
  ROOT_DCH_ADMIN_ROLE_ID_LIST,
  USER_ROLE_IDS_WITH_UAT_ROLE_CREATION,
  USER_ROLE_ID_MAP,
  USER_ROLE_IDS_THAT_CAN_BE_CREATED_BY_SITE_ADMIN,
  USER_ROLE_IDS_WITH_LIMITED_WRITE_USER_PERMISSIONS,
  USER_PAGE_TYPE_MAP
} from 'utils/constants'
import { getUserRoleIdByStudy } from 'utils/misc'

const selectUser = state => state.user
const selectStudy = state => state.study

const useRolesList = (pageType) => {
  const user = useSelector(selectUser)
  const study = useSelector(selectStudy)
  const currentStudy = study?.currentStudy

  const studyID = currentStudy?.id
  const config = currentStudy?.config || {}

  // Memoized feature flags
  const {
    case_management: hasCaseManagement,
    clinro: hasClinro,
    consent_required: hasConsent,
    insights: hasInsights,
    study_promotion: hasStudyPromotion,
  } = config

  const currentRoleId = studyID ? getUserRoleIdByStudy(user, studyID, hasStudyPromotion) : 7

  const showStudyPromotionRoles = true // TODO: this seems hardcoded

  const currentUserIsRootOrDchAdmin = ROOT_DCH_ADMIN_ROLE_ID_LIST.includes(currentRoleId)
  const isAllowedToCreateImpartialWitness = IMPARTIAL_WITNESS_ALLOWED_CREATORS_LIST.includes(currentRoleId)
  const isAllowedToCreateUATRoles = USER_ROLE_IDS_WITH_UAT_ROLE_CREATION.includes(currentRoleId)
  const isCurrentUserWithLimitedWriteAccess = USER_ROLE_IDS_WITH_LIMITED_WRITE_USER_PERMISSIONS.includes(currentRoleId)

  const roles = useMemo(() => {
    let roleArr = [...AVAILABLE_USER_OPTIONS]

    const excludeRoles = new Set()

    // Exclude based on study features
    // Case Manager: Hide when Study Promotion is enabled, or when case_management is disabled
    if (hasStudyPromotion || !hasCaseManagement) {
      excludeRoles.add(USER_ROLE_ID_MAP.caseManager)
    }
    if (!hasClinro) CLINICIAN_ROLES_ID_LIST.forEach(id => excludeRoles.add(id))
    if (!hasInsights) excludeRoles.add(USER_ROLE_ID_MAP.reporter)

    // Impartial Witness logic
    const canShowImpartialWitness = hasClinro && hasConsent && isAllowedToCreateImpartialWitness
    if (!canShowImpartialWitness) excludeRoles.add(USER_ROLE_ID_MAP.impartialWitness)

    // UAT/DEV roles filtering
    const uatAndDevRoles = [
      USER_ROLE_ID_MAP.siteCoordinatorUAT,
      USER_ROLE_ID_MAP.clinicalCoordinatorUAT,
      USER_ROLE_ID_MAP.sponsorUAT,
      USER_ROLE_ID_MAP.siteCoordinatorDEV,
      USER_ROLE_ID_MAP.clinicalCoordinatorDEV,
      USER_ROLE_ID_MAP.sponsorDEV,
    ]
    if (!hasStudyPromotion || !isAllowedToCreateUATRoles || !showStudyPromotionRoles) {
      uatAndDevRoles.forEach(id => excludeRoles.add(id))
    }

    // Clinical Coordinator roles can only be created by DCH users.
    if (!currentUserIsRootOrDchAdmin) {
      ;[
        USER_ROLE_ID_MAP.clinicalCoordinator,
        USER_ROLE_ID_MAP.clinicalCoordinatorUAT,
        USER_ROLE_ID_MAP.clinicalCoordinatorDEV,
      ].forEach(id => excludeRoles.add(id))
    }

    // If study has promotion enabled, exclude a large set of roles (create and edit must match)
    if (hasStudyPromotion && pageType !== USER_PAGE_TYPE_MAP.profile) {
      [
        USER_ROLE_ID_MAP.researcher,
        USER_ROLE_ID_MAP.enroller,
        USER_ROLE_ID_MAP.auditor,
        USER_ROLE_ID_MAP.clinRoI,
        USER_ROLE_ID_MAP.clinRoII,
        USER_ROLE_ID_MAP.clinRoIII,
        USER_ROLE_ID_MAP.reporter,
        USER_ROLE_ID_MAP.siteUserManagementAdmin,
        USER_ROLE_ID_MAP.impartialWitness,
        USER_ROLE_ID_MAP.sponsorUserMonitor,
      ].forEach(id => excludeRoles.add(id))
    }

    // Role creation restrictions based on current user
    if (isCurrentUserWithLimitedWriteAccess) {
      roleArr = roleArr.filter(role => USER_ROLE_IDS_THAT_CAN_BE_CREATED_BY_SITE_ADMIN.includes(role.role_id))
    } else {
      // Only Root/DCH Admins can create SiteUserManagementAdmin
      if (!currentUserIsRootOrDchAdmin) excludeRoles.add(USER_ROLE_ID_MAP.siteUserManagementAdmin)
    }

    // Final filter pass
    return roleArr.filter(role => role.key && !excludeRoles.has(role.role_id))
  }, [
    currentRoleId,
    hasCaseManagement,
    hasClinro,
    hasConsent,
    hasInsights,
    hasStudyPromotion,
    isAllowedToCreateImpartialWitness,
    isAllowedToCreateUATRoles,
    isCurrentUserWithLimitedWriteAccess,
    currentUserIsRootOrDchAdmin,
    showStudyPromotionRoles,
    pageType
  ])

  return roles
}

export default useRolesList
